import { ContentSearchPanel } from "../ContentSearchPanel";
import { useContentSearch } from "../../hooks/useContentSearch";
import {
    BulkListContentNavbarWrapper,
    ManualBulkContentListWrapper,
    ManualBulkMobileFilters,
    ManualBulkMobileTypeSrcWrapper,
    ManualBulkRightSidePanel,
    ManualSelectBulkWrapper
} from "./ManualBulkSelectContent.styles";
import { ContentList } from "../ContentList";
import { SEARCH_TERM_DEFAULT_ORDER_BY, SearchContentFormik, ShareContentSearchProps } from "../../types/content-search";
import { ListContentNavbar } from "../ListContentNavbar";
import { ContentSort } from "../ContentSort";
import { V3CheckBox } from "../OptionInput";
import { V3Button } from "../Button";
import { V3ButtonColor } from "../Button/V3Button.types";
import { RightAlignPanel } from "../ReusableStyledComponents/RightAlignPanel";
import { useCustomScrollShadows } from "../../hooks/customScrollShadows";
import { FakeSelectWithModal } from "../FakeSelect";
import { V3TextInput } from "../TextInput/V3TextInput";
import { V3IconMagnifier } from "../Icons";
import { VerticalSpacing } from "../VerticalSpacing";
import { Collection, CollectionRuleResponse, ModifyCollectionContent, RuleOperator } from "@authory-packages/core";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { CookieContext } from "../../context/cookieContext";
import { getAuthenticatedUserClient } from "../../utils/getAuthenticatedUser";
import { buildContentFilterQueryParams } from "../../pagelayouts/content/utils/buildContentFilterQueryParams";
import { getContentInfiniteQuerie } from "../../utils/query-utils/contentInfiniteQuerie";
import { getAddRemoveItemFromCollectionMutation } from "../../utils/mutation-utils/collections/AddRemoveItemFromCollection";
import { useQueryClient } from "@tanstack/react-query";
import { Queries } from "../../types/queries";
import { ModalLoadingSpinner } from "../ReusableStyledComponents/ModalLoadingSpinner";
import { generateCollectionRules } from "../../utils/generateCollectionRules";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { SRC_SHOW_LIMIT } from "../ContentSearchPanel/ContentSearchPanel.utils";
import toast from "react-hot-toast";

interface ManualBulkSelectContent extends ShareContentSearchProps {
    onCloseHandler: () => void;
    onBackHandler: () => void;
    manualAddItemsToCollection: (articleIds: string[], data: SearchContentFormik, masterCheckBoxSelection: boolean) => Promise<void>;
    subLabelCallback: (label: string) => void;
}

export const ManualBulkSelectContent = ({
    sources,
    searchArgs,
    onSearchFormSubmit,
    typeCounts,
    contentLoading,
    contentCanFetchmore,
    contentLoadmore,
    content,
    onBackHandler,
    manualAddItemsToCollection,
    subLabelCallback
}: ManualBulkSelectContent) => {
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const {
        formattedFromDate,
        formattedToDate,
        searchContentFormik,
        IS_ACTIVE_FILTER_SEARCH,
        handleContentFormikArray,
        clearContentPageLeftFilters,
        searchKey,
        resetSearchKey
    } = useContentSearch(searchArgs, onSearchFormSubmit, true);

    const [masterCheckBoxSelected, setMasterCheckboxSelected] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const searchPanelScrollShadows = useCustomScrollShadows();
    const { onScroll, ref, style, shadowStates, scrollElem } = useCustomScrollShadows();

    const contentLength = content.length ? content[0].filteredCount : 0;
    const HAS_NO_RESULTS = contentLength === 0 && !contentCanFetchmore;
    const IS_ACTIVE_TEXT_SEARCH = !!searchArgs.text;
    const IS_ACTIVE_SEARCH = IS_ACTIVE_TEXT_SEARCH || IS_ACTIVE_FILTER_SEARCH;

    useEffect(() => {
        subLabelCallback(`${contentLength.toLocaleString()} items ${IS_ACTIVE_SEARCH ? "found" : ""}`);
    }, [IS_ACTIVE_SEARCH, contentLength]);

    // each time the search form is submitted, we reset the master checkbox
    useEffect(() => {
        if (masterCheckBoxSelected) setMasterCheckboxSelected(false);
    }, [searchContentFormik.submitCount]);

    const numItemsSelectionHas = masterCheckBoxSelected ? contentLength - selectedItems.length : selectedItems.length ? selectedItems.length : 0;

    return (
        <ManualSelectBulkWrapper $isAboveSrcLimit={sources.length > SRC_SHOW_LIMIT}>
            <div ref={searchPanelScrollShadows.ref} onScroll={searchPanelScrollShadows.onScroll} style={searchPanelScrollShadows.style}>
                <ContentSearchPanel
                    title={"Sources"}
                    items={sources
                        .map((src) => ({ label: src.publicationName || "", value: src.canonicalSlug }))
                        .sort((a, b) => a.label.localeCompare(b.label))}
                    selectedItems={searchContentFormik.values.source}
                    handleItemChange={handleContentFormikArray("source")}
                    selectedTypes={searchContentFormik.values.type}
                    handleTypeChange={handleContentFormikArray("type")}
                    typeCounts={typeCounts}
                    setItems={(items) => {
                        searchContentFormik.setFieldValue("source", items);
                    }}
                    setTypes={(types) => {
                        searchContentFormik.setFieldValue("type", types);
                    }}
                    onClearHandler={clearContentPageLeftFilters}
                    onSubmitHandler={searchContentFormik.submitForm}
                    onSetFieldValue={searchContentFormik.setFieldValue}
                    fromDate={formattedFromDate}
                    toDate={formattedToDate}
                    showSourceTooltip={false}
                    isManualBulkAdd
                />
            </div>
            <ManualBulkRightSidePanel>
                <ManualBulkMobileFilters>
                    <form onSubmit={searchContentFormik.submitForm} action=".">
                        <VerticalSpacing bottom={12}>
                            <V3TextInput
                                type="search"
                                placeholder={"Search...."}
                                isRounded
                                icon={
                                    <V3IconMagnifier
                                        onClick={() => {
                                            (document.activeElement as HTMLElement).blur();
                                            searchContentFormik.submitForm();
                                        }}
                                    />
                                }
                                value={searchContentFormik.values.text}
                                onChange={searchContentFormik.handleChange}
                                name="text"
                                autoComplete="off"
                                onKeyDown={(e) => {
                                    if (e.code === "Enter") {
                                        (document.activeElement as HTMLElement).blur();
                                        searchContentFormik.submitForm();
                                    }
                                }}
                                hasClearIcon={!!searchContentFormik.values.text.length}
                                onClearIconHandler={() => {
                                    searchContentFormik.setFieldValue("text", "");
                                    searchContentFormik.submitForm();
                                }}
                            />
                        </VerticalSpacing>
                        <ManualBulkMobileTypeSrcWrapper>
                            <div>
                                <FakeSelectWithModal
                                    label="Sources"
                                    placeholder="All Sources"
                                    items={sources.map((src) => ({ label: src.publicationName || "", value: src.canonicalSlug }))}
                                    selectedItems={searchContentFormik.values.source}
                                    onModalSubmitClose={(selectedModalItems) => {
                                        searchContentFormik.setFieldValue("source", selectedModalItems);
                                        searchContentFormik.submitForm();
                                    }}
                                />
                            </div>
                            <div>
                                <FakeSelectWithModal
                                    label="Content types"
                                    placeholder="All Content Types"
                                    items={[]}
                                    useTypesList
                                    selectedItems={searchContentFormik.values.type}
                                    onModalSubmitClose={(selectedModalItems) => {
                                        searchContentFormik.setFieldValue("type", selectedModalItems);
                                        searchContentFormik.submitForm();
                                    }}
                                />
                            </div>
                        </ManualBulkMobileTypeSrcWrapper>
                    </form>
                </ManualBulkMobileFilters>
                <BulkListContentNavbarWrapper>
                    <ListContentNavbar
                        isBulkModalVariant
                        mainLabel={
                            !HAS_NO_RESULTS ? (
                                <V3CheckBox
                                    name={"master_checkbox_bulk_add"}
                                    id={"master_checkbox_bulk_add"}
                                    onChange={() => {
                                        // is not selected, or selected and no manual items chosen, reverse the master check box state!
                                        if (!masterCheckBoxSelected || (masterCheckBoxSelected && selectedItems.length === 0)) {
                                            setMasterCheckboxSelected(!masterCheckBoxSelected);
                                        }
                                        // reset the list
                                        resetSearchKey();
                                    }}
                                    checked={masterCheckBoxSelected && selectedItems.length === 0}
                                    isPartialCheck={selectedItems.length > 0}
                                    label={
                                        <V3BodyGrey48 style={{ marginLeft: 17, position: "relative", top: -1 }}>
                                            <strong>Select all</strong>
                                        </V3BodyGrey48>
                                    }
                                />
                            ) : (
                                <></>
                            )
                        }
                        searchValue={searchContentFormik.values.text}
                        onChange={(e) => {
                            if (searchContentFormik.values.sort === undefined)
                                searchContentFormik.setFieldValue("sort", SEARCH_TERM_DEFAULT_ORDER_BY);
                            searchContentFormik.handleChange(e);
                        }}
                        setSearchValue={(val) => {
                            if (val === "") searchContentFormik.setFieldValue("sort", undefined);
                            searchContentFormik.setFieldValue("text", val);
                        }}
                        submitForm={searchContentFormik.submitForm}
                        isActiveSearch={IS_ACTIVE_SEARCH}
                        isActiveFilterSearch={IS_ACTIVE_FILTER_SEARCH}
                        onClearHandler={!HAS_NO_RESULTS ? clearContentPageLeftFilters : undefined}
                        optionalDesktopcontent={<ContentSort isActiveTextSearch={IS_ACTIVE_TEXT_SEARCH} searchContentFormik={searchContentFormik} />}
                    />
                </BulkListContentNavbarWrapper>
                <ManualBulkContentListWrapper
                    ref={ref}
                    onScroll={onScroll}
                    style={{
                        ...style,
                        borderBottom: shadowStates.bottomShadow ? undefined : "1px solid #DDDFE3"
                    }}
                >
                    <ContentList
                        items={content}
                        articlesLoading={contentLoading}
                        listReset={searchKey}
                        collections={[]}
                        hasNoResults={HAS_NO_RESULTS}
                        onClearResultsHandler={() => {
                            clearContentPageLeftFilters();
                        }}
                        isActiveSearch={IS_ACTIVE_SEARCH}
                        contentLoadmore={contentLoadmore}
                        contentCanFetchmore={contentCanFetchmore}
                        isSelectOnly
                        onContentSelectedChange={(selectedItems) => {
                            setSelectedItems(selectedItems);
                        }}
                        excludeMode={masterCheckBoxSelected}
                        onFullContentSelected={() => {
                            setMasterCheckboxSelected(true);
                            resetSearchKey();
                        }}
                        onFullContentDeselected={() => {
                            setMasterCheckboxSelected(false);
                            resetSearchKey();
                        }}
                        skipFooterControl
                        scrollRoot={scrollElem}
                    />
                </ManualBulkContentListWrapper>
                <div>
                    <RightAlignPanel>
                        {modalSubmitting ? (
                            <ModalLoadingSpinner />
                        ) : (
                            <>
                                <div>
                                    <V3Button text="Back" minWidth={135} autoWidth onClick={onBackHandler} color={V3ButtonColor.secondary} />
                                </div>
                                <div>
                                    <V3Button
                                        text={`Add ${numItemsSelectionHas.toLocaleString()} items`}
                                        autoWidth
                                        minWidth={135}
                                        disabled={numItemsSelectionHas === 0}
                                        onClick={async () => {
                                            try {
                                                setModalSubmitting(true);
                                                await manualAddItemsToCollection(selectedItems, searchContentFormik.values, masterCheckBoxSelected);
                                                setModalSubmitting(false);
                                            } catch {
                                                setModalSubmitting(false);
                                            }
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </RightAlignPanel>
                </div>
            </ManualBulkRightSidePanel>
        </ManualSelectBulkWrapper>
    );
};

interface ManualBulkSelectContainerProps {
    onCloseHandler: () => void;
    onBackHandler: () => void;
    collection?: Collection;
    onBulkSelectionComplete?: (rules: CollectionRuleResponse[]) => void;
    subLabelCallback: (label: string) => void;
}

export const ManualBulkSelectContainer = ({
    onCloseHandler,
    onBackHandler,
    collection,
    onBulkSelectionComplete,
    subLabelCallback
}: ManualBulkSelectContainerProps) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const token = useContext(CookieContext);
    const { authenticatedUser } = getAuthenticatedUserClient(token, router, true);
    const [searchState, setSearchState] = useState<SearchContentFormik>({
        source: [],
        text: "",
        type: [],
        subAccount: [],
        from: null,
        to: null
    });

    // Add item to collection mutation
    const addItemToCollectionMutation = getAddRemoveItemFromCollectionMutation(queryClient);

    const manualAddItemsToCollection = async (articleIds: string[], data: SearchContentFormik, masterCheckBoxSelection: boolean) => {
        if (!token || !authenticatedUser.data?.slug || !collection) return;

        const hasSearchTerm = !!data.text.length;
        const hasSource = !!data.source.length;
        const hasType = !!data.type.length;
        const numRules = (hasSearchTerm ? 1 : 0) + (hasSource ? 1 : 0) + (hasType ? 1 : 0);

        const payload: ModifyCollectionContent = {};

        // always add articles if selected, either to include or exclude based on being a filter based selection
        if (!!articleIds.length) {
            payload.articleIds = articleIds;
        }

        // filter based selection
        if (masterCheckBoxSelection) {
            const operator = numRules > 1 ? RuleOperator.All : RuleOperator.Any;

            let rules = generateCollectionRules(data.source, data.type, data.text);

            if (rules.length === 0) {
                // no filters set, means user wants to add it all so we pick content from all sources
                rules = generateCollectionRules(
                    authenticatedUser.data.sources.map((src) => src.canonicalSlug),
                    [],
                    ""
                );
            }

            payload.customBulkAdd = {
                operator,
                rules
            };
        }

        await addItemToCollectionMutation.mutateAsync({
            token,
            userSlug: authenticatedUser.data?.slug as string,
            collectionSlug: collection.canonicalSlug,
            payload,
            add: true,
            skipContentInvalidation: true
        });

        toast.success("Content added to collection");

        if (masterCheckBoxSelection && numRules > 0 && payload.customBulkAdd?.rules) {
            // Attempt to automate some rules for the user
            onBulkSelectionComplete &&
                onBulkSelectionComplete(
                    payload.customBulkAdd?.rules.map((rl, index) => ({
                        ...rl,
                        // id is not important because it's not a real rule, it's just something we need to present to the user
                        id: index.toString()
                    }))
                );
        } else {
            // flow is finished, just close the modal
            onCloseHandler();
        }

        // allow time for modal to close so we dont request these content queries again
        setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: [Queries.User] });
            queryClient.invalidateQueries({ queryKey: [Queries.Collection] });
            queryClient.invalidateQueries({ queryKey: [Queries.Content] });
        }, 20);
    };

    const params = buildContentFilterQueryParams(searchState);
    const items = getContentInfiniteQuerie(token, authenticatedUser.data?.slug, params);

    return (
        <ManualBulkSelectContent
            content={items.data?.pages || []}
            contentLoading={items.isFetching}
            contentLoadmore={items.fetchNextPage}
            contentCanFetchmore={items.hasNextPage || false}
            sources={authenticatedUser.data?.sources || []}
            searchArgs={searchState}
            onSearchFormSubmit={setSearchState}
            onCloseHandler={onCloseHandler}
            onBackHandler={onBackHandler}
            manualAddItemsToCollection={manualAddItemsToCollection}
            subLabelCallback={subLabelCallback}
        />
    );
};
